import * as z from "zod";
import { TEXT_CONTROL_CHARACTERS } from "../../team-calendar-entry-fields";
import { TeamCalendarEntryAgendaItemType } from "../agenda-item-type";
import { defineAgendaItemSchema } from "../base";

export enum DetectedPromotionStatus {
  Active = "active",
  Ended = "ended",
}

/** Provenance for a promotion found on the brand's homepage. ISO strings: it lives in jsonb. */
export const detectedPromotionSchema = z.object({
  /** Stable slug the detector assigns so later scrapes find the same entry. */
  key: z.string(),
  sourceUrl: z.string(),
  discountCode: z.string().nullish(),
  /** End date the site itself states, when it states one. */
  statedEndsAt: z.iso.datetime().nullish(),
  status: z.enum(DetectedPromotionStatus),
  lastSeenAt: z.iso.datetime(),
  /** Last detector write; a later entry `updatedAt` means the merchant edited it. */
  syncedAt: z.iso.datetime(),
});

export type DetectedPromotion = z.infer<typeof detectedPromotionSchema>;

/**
 * A promotion attached to a calendar entry. Currently a free-form text
 * block describing the promotion — surfaced as LLM context during the
 * entry's active window. An entry can have multiple promotions.
 */
export const promotionAgendaItemSchema = defineAgendaItemSchema(
  TeamCalendarEntryAgendaItemType.Promotion,
  {
    details: z.string().nullish(),
    detection: detectedPromotionSchema.nullish(),
  },
);

export type PromotionAgendaItem = z.infer<typeof promotionAgendaItemSchema>;

export const PROMOTION_DETAILS_MAX_LENGTH = 2000;

/** What a caller may write on a promotion. `detection` belongs to the promotion detector. */
export const promotionAgendaItemWriteSchema = z.object({
  details: z
    .string()
    .trim()
    .min(1)
    .max(PROMOTION_DETAILS_MAX_LENGTH)
    .refine((value) => !TEXT_CONTROL_CHARACTERS.test(value), {
      message: "Must not contain control characters",
    }),
});
